import React, { useMemo, useState } from "react";

import { ZoneId } from "@js-joda/core";
import { useClearExpiredAlerts, useGetAlertHistory } from "api/api-hooks/alerts-hooks";
import { ALERT_TYPE_OPTIONS, AlertDTO, TARGET_TYPE_OPTIONS } from "api/model/alerts/alert-types";
import DataTable from "components/basic/data-table/DataTable";
import Search from "components/basic/search/Search";
import { TableColumn } from "components/pages/measurement-devices/utils/types";
import { PermissionGuard } from "components/wrappers/PermissionGuard";
import { useTimezone } from "hooks/useTimezone";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { T } from "services/lang/LanguageService";
import { fmtDatetime } from "utils/time-utils";
import PriorityTag from "./components/PriorityTag";

const AlertHistoryTable: React.FC = () => {
  const { data: alerts, isLoading } = useGetAlertHistory();
  const clearExpiredMutation = useClearExpiredAlerts();
  const [search, setSearch] = useState("");
  const tz = useTimezone();

  const filteredAlerts = useMemo(() => {
    if (!alerts) return [];
    if (!search) return alerts;
    return alerts.filter(alert => {
      const searchLower = search.toLowerCase();
      return (
        alert.message?.toLowerCase().includes(searchLower) ||
        alert.shortMessage?.toLowerCase().includes(searchLower) ||
        alert.id.toString().includes(searchLower)
      );
    });
  }, [alerts, search]);

  const handleClearExpired = () => {
    confirmDialog({
      message: T("alerts.confirm-clear-expired"),
      header: T("confirm-dialog.confirmation"),
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        clearExpiredMutation.mutate();
      },
    });
  };

  const columns = useMemo(() => getColumns(tz.display), [tz.display]);

  return (
    <DataTable
      columns={columns}
      data={filteredAlerts}
      tableConfig={{
        loading: isLoading,
        scrollable: true,
        scrollHeight: "60vh",
        paginator: true,
        rows: 25,
        rowsPerPageOptions: [25, 50, 100],
        header: (
          <div className="flex justify-content-between align-items-center">
            <Search value={search} onChange={setSearch} />
            <PermissionGuard permissions="ADMIN">
              <Button
                label={T("action.clear-expired")}
                icon="pi pi-trash"
                severity="danger"
                onClick={handleClearExpired}
                loading={clearExpiredMutation.isPending}
              />
            </PermissionGuard>
          </div>
        ),
        emptyMessage: T("alerts.no-history"),
      }}
    />
  );
};

const getColumns = (zoneId: ZoneId): TableColumn<AlertDTO>[] => [
  {
    field: "alertType",
    header: T("word.type"),
    columnKey: "alertType",
    body: row => ALERT_TYPE_OPTIONS.find(o => o.value === row.alertType)?.label,
    className: "w-10rem",
  },
  {
    field: "priority",
    header: T("word.priority"),
    columnKey: "priority",
    body: row => <PriorityTag priority={row.priority} />,
    className: "w-8rem",
  },
  {
    field: "shortMessage",
    header: T("word.message"),
    columnKey: "shortMessage",
    body: row => (
      <div
        className="white-space-nowrap overflow-hidden text-overflow-ellipsis"
        style={{ maxWidth: "20rem" }}
      >
        {row.shortMessage || row.message || "-"}
      </div>
    ),
  },
  {
    field: "targetType",
    header: T("word.target"),
    columnKey: "targetType",
    body: row => TARGET_TYPE_OPTIONS.find(o => o.value === row.targetType)?.label,
    className: "w-10rem",
  },
  {
    field: "createdAt",
    header: T("word.created"),
    columnKey: "createdAt",
    body: row => row.createdAt && fmtDatetime(row.createdAt, zoneId),
    sortable: true,
    className: "w-12rem",
  },
  {
    field: "timeoutAt",
    header: T("word.expired"),
    columnKey: "timeoutAt",
    body: row => row.timeoutAt && fmtDatetime(row.timeoutAt, zoneId),
    sortable: true,
    className: "w-12rem",
  },
];

export default AlertHistoryTable;
